/**
 * Vertragsverlauf eines Mietverhältnisses: Kaltmiete, Vorauszahlungen und Personenzahl
 * ändern sich im Lauf der Zeit (Mieterhöhung, Anpassung der Vorauszahlungen nach § 560
 * Abs. 4 BGB, Ein- oder Auszug von Mitbewohnern). Jede Änderung gilt ab einem Stichtag.
 *
 * Grundwerte = Stand ab Mietbeginn (Felder des Mietverhältnisses). Ein Vertragsstand
 * enthält die vollständigen Werte ab seinem Stichtag.
 *
 * Regel für Monatsbeträge: Vorauszahlung und Miete werden monatlich geschuldet. Für einen
 * Kalendermonat gilt der Stand am ersten Tag, an dem das Mietverhältnis in diesem Monat
 * bestand (in der Regel der Monatserste).
 */

import { Cent } from './geld';
import { ISODate, Zeitraum, schnitt, tage } from './zeit';

export interface Vertragswerte {
  kaltmieteCent: Cent;
  vzNebenkostenCent: Cent;
  vzHeizkostenCent: Cent;
  personenzahl: number;
}

export interface Vertragsstand extends Vertragswerte {
  /** gilt ab diesem Tag (inklusive) */
  ab: ISODate;
}

export interface Vertragsverlauf {
  beginn: ISODate;
  /** null = laufend */
  ende: ISODate | null;
  grundwerte: Vertragswerte;
  staende?: Vertragsstand[];
}

function sortiert(staende: Vertragsstand[] | undefined): Vertragsstand[] {
  return [...(staende ?? [])].sort((a, b) => (a.ab < b.ab ? -1 : a.ab > b.ab ? 1 : 0));
}

/** Gültige Werte an einem Tag. */
export function standAm(v: Vertragsverlauf, datum: ISODate): Vertragswerte {
  let werte = v.grundwerte;
  for (const s of sortiert(v.staende)) {
    if (s.ab <= datum) werte = s;
    else break;
  }
  return {
    kaltmieteCent: werte.kaltmieteCent,
    vzNebenkostenCent: werte.vzNebenkostenCent,
    vzHeizkostenCent: werte.vzHeizkostenCent,
    personenzahl: werte.personenzahl,
  };
}

function monatsErster(d: ISODate): ISODate {
  return `${d.slice(0, 7)}-01`;
}

function naechsterMonat(d: ISODate): ISODate {
  const [j, m] = d.split('-').map(Number);
  const nj = m === 12 ? j + 1 : j;
  const nm = m === 12 ? 1 : m + 1;
  return `${String(nj).padStart(4, '0')}-${String(nm).padStart(2, '0')}-01`;
}

export interface Monatssoll {
  monate: number;
  kaltmieteCent: Cent;
  vzNebenkostenCent: Cent;
  vzHeizkostenCent: Cent;
}

/**
 * Summe der monatlich geschuldeten Beträge im Zeitraum. Ein angefangener Monat zählt voll,
 * sofern das Mietverhältnis an mindestens einem Tag des Monats bestand (wie
 * `monateBeruehrt`); maßgeblich ist der Stand am ersten belegten Tag des Monats.
 */
export function sollImZeitraum(v: Vertragsverlauf, zeitraum: Zeitraum): Monatssoll {
  const ergebnis: Monatssoll = { monate: 0, kaltmieteCent: 0, vzNebenkostenCent: 0, vzHeizkostenCent: 0 };
  const s = schnitt({ von: v.beginn, bis: v.ende ?? zeitraum.bis }, zeitraum);
  if (!s) return ergebnis;
  let monat = monatsErster(s.von);
  while (monat <= s.bis) {
    const ersterTag = monat < s.von ? s.von : monat;
    const w = standAm(v, ersterTag);
    ergebnis.monate += 1;
    ergebnis.kaltmieteCent += w.kaltmieteCent;
    ergebnis.vzNebenkostenCent += w.vzNebenkostenCent;
    ergebnis.vzHeizkostenCent += w.vzHeizkostenCent;
    monat = naechsterMonat(monat);
  }
  return ergebnis;
}

/**
 * Personentage im Zeitraum: Personenzahl × Tage, tagegenau über alle Änderungen.
 * Grundlage für die Umlage nach Personen bei wechselnder Belegung.
 */
export function personentage(v: Vertragsverlauf, zeitraum: Zeitraum): number {
  const s = schnitt({ von: v.beginn, bis: v.ende ?? zeitraum.bis }, zeitraum);
  if (!s) return 0;
  const grenzen = sortiert(v.staende)
    .map((x) => x.ab)
    .filter((d) => d > s.von && d <= s.bis);
  let summe = 0;
  let von = s.von;
  for (const g of [...grenzen, null]) {
    const bis = g === null ? s.bis : vorTag(g);
    if (bis >= von) summe += standAm(v, von).personenzahl * tage({ von, bis });
    if (g !== null) von = g;
  }
  return summe;
}

function vorTag(d: ISODate): ISODate {
  const [j, m, t] = d.split('-').map(Number);
  return new Date(Date.UTC(j, m - 1, t - 1)).toISOString().slice(0, 10);
}
